import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { NotificationBell } from './NotificationBell';
import { Button } from './ui/button';
import { Gavel, Plus, User, LogOut, Shield, Heart } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.is_admin || user?.email === 'brorbyj@gmail.com';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-xl border-b border-border supports-[backdrop-filter]:bg-white/60">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Gavel className="h-6 w-6" strokeWidth={1.5} />
            <span className="font-heading font-bold text-2xl tracking-tighter">GREEN FROG AUCTIONS</span>
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            <Link to="/browse" className="text-sm font-medium hover:text-primary transition-colors" data-testid="browse-link">
              Browse Auctions
            </Link>
            {user && (
              <Link to="/watchlist" className="text-sm font-medium hover:text-primary transition-colors" data-testid="watchlist-link">
                Watchlist
              </Link>
            )}
            {isAdmin && (
              <Link to="/create" className="text-sm font-medium hover:text-primary transition-colors" data-testid="create-auction-link">
                Create Auction
              </Link>
            )}
          </nav>

          <div className="flex items-center gap-3">
            {user ? (
              <>
                {isAdmin && (
                  <Button
                    onClick={() => navigate('/create')}
                    className="h-9 px-6 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-medium transition-all active:scale-95 hidden md:flex"
                    data-testid="create-auction-button"
                  >
                    <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
                    Create
                  </Button>
                )}
                <Button
                  onClick={() => navigate('/watchlist')}
                  variant="ghost"
                  className="h-9 w-9 rounded-sm p-0 hidden md:flex"
                  data-testid="watchlist-button"
                >
                  <Heart className="h-5 w-5" strokeWidth={1.5} />
                </Button>
                <NotificationBell />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="h-9 w-9 rounded-sm p-0"
                      data-testid="user-menu-button"
                    >
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="h-7 w-7 rounded-sm" />
                      ) : (
                        <User className="h-5 w-5" strokeWidth={1.5} />
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="flex flex-col gap-1 p-2">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium font-mono">{user.name}</p>
                        {isAdmin && <Shield className="h-3 w-3 text-primary" />}
                      </div>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                      {isAdmin && <p className="text-xs text-primary font-medium">Admin</p>}
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="dashboard-menu-item">
                      <User className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      {isAdmin ? 'Admin Dashboard' : 'My Bids'}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/watchlist')} data-testid="watchlist-menu-item">
                      <Heart className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      Watchlist
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem onClick={() => navigate('/create')} className="md:hidden">
                        <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
                        Create Auction
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                      <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button
                  onClick={() => navigate('/login')}
                  variant="ghost"
                  className="h-9 px-4 rounded-sm font-medium"
                  data-testid="login-button"
                >
                  Login
                </Button>
                <Button
                  onClick={() => navigate('/signup')}
                  className="h-9 px-6 rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-medium transition-all active:scale-95"
                  data-testid="signup-button"
                >
                  Sign Up
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
